import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormSelect2 from 'components-desktop/FormSelect2';
import Panel from 'components-desktop/Panel';
import misc from 'misc/misc';
import helpers from 'misc/helpers';
import TableEvvs from './components/TableEvvs';

const TEMPLATE_URL = 'https://starzle-hhaplus-001.oss-us-east-1.aliyuncs.com/evv/duty_sheet_template_v5.xlsx';

const TASK_TO_ROW = {
  100: '11',
  101: '12',
  102: '13',
  103: '14',
  106: '15',
  107: '16',
  108: '17',
  109: '18',
  110: '19',
  111: '20',
  112: '21',
  113: '22',
  114: '23',
  115: '24',
  116: '25',
  117: '26',
  201: '27',
  202: '28',
  203: '29',
  204: '30',
  205: '31',
  206: '32',
  207: '33',
  208: '34',
  300: '35',
  301: '36',
  302: '37',
  305: '38',
  306: '39',
  311: '40',
  400: '41',
  411: '42',
  413: '43',
  414: '44',
  500: '45',
  501: '46',
  502: '47',
  506: '48',
  508: '49',
  509: '50',
  511: '51',
  514: '52',
};

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class ExportDutySheetPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      caregiverNames: [],
      patientNames: [],

      tableKey: null,
      evvs: null,

      isSearching: false,
      isExporting: false,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Export Duty Sheet';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise2 = this.callServiceForObjects('/patient/get_id_names');

    const results = await Promise.all([promise1, promise2]);

    this.setState({
      caregiverNames: results[0],
      patientNames: results[1],
    });
  }

  async searchEvvs(values) {
    try {
      this.setState({
        isSearching: true,
      });

      // Get fromTime match start of week. Get toTime match end of week.
      const fromWeekStart = values.fromTime.startOf('isoWeek'); // Monday.
      const toWeekEnd = values.toTime.endOf('isoWeek'); // Sunday.

      const params = {
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        patientId: helpers.extraFormSelectValue(values, 'patientId'),
        fromTime: misc.toDateTimeString(fromWeekStart),
        toTime: misc.toDateTimeString(toWeekEnd),
      };
      const evvs = await this.callServiceForRemoteObjects('/evv/get_duty_sheet_list', params);

      this.setState({
        tableKey: `${Date.now()}_${Math.random()}`,
        evvs,
      });
    } finally {
      this.setState({
        isSearching: false,
      });
    }
  }

  /* eslint-disable no-await-in-loop */
  async exportDutySheet(values) {
    try {
      this.setState({
        isExporting: true,
      });

      // Get fromTime match start of week. Get toTime match end of week.
      const fromWeekStart = values.fromTime.clone().startOf('isoWeek'); // Monday.
      const toWeekEnd = values.toTime.clone().endOf('isoWeek'); // Sunday.

      const params = {
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        patientId: helpers.extraFormSelectValue(values, 'patientId'),
        fromTime: misc.toDateTimeString(fromWeekStart),
        toTime: misc.toDateTimeString(toWeekEnd),
      };
      const evvs = await this.callServiceForRemoteObjects('/evv/get_duty_sheet_list', params);

      this.setState({
        tableKey: `${Date.now()}_${Math.random()}`,
        evvs,
      });

      // Get other useful info.
      const agency = await this.callServiceForRemoteObject('/agency/get_model');
      const patient = await this.callServiceForRemoteObject('/patient/get_model', { id: params.patientId });
      const caregiver = await this.callServiceForRemoteObject('/caregiver/get_model', { id: params.caregiverId });
      const sampleEvv = evvs.length > 0 ? evvs[0] : null;

      // Download template to write on.
      const workbook = await this.downloadDutySheetTemplate();

      // Split evvs into each week.
      let currentFromTime = fromWeekStart.clone();
      while (currentFromTime.isBefore(toWeekEnd)) {
        const currentToTime = currentFromTime.clone().add(7, 'd').subtract(1, 's');

        // eslint-disable-next-line no-loop-func
        const evvsThisWeek = evvs.filter(e => e.getLong('time') >= currentFromTime.valueOf() &&
          e.getLong('time') <= currentToTime.valueOf());

        // Create a new worksheet.
        const sheetToClone = workbook.worksheets[0];
        const newWorksheet = workbook.addWorksheet('Sheet');
        newWorksheet.model = sheetToClone.model;
        Object.keys(sheetToClone._merges).forEach((masterName) => {
          const dimensions = sheetToClone._merges[masterName].model;
          const master = newWorksheet.getCell(dimensions.top, dimensions.left);
          for (let i = dimensions.top; i <= dimensions.bottom; i++) {
            for (let j = dimensions.left; j <= dimensions.right; j++) {
              // merge all but the master cell
              if ((i > dimensions.top) || (j > dimensions.left)) {
                newWorksheet.getCell(i, j).merge(master);
              }
            }
          }
        });
        newWorksheet.name = `${currentFromTime.format('MM-DD')} - ${currentToTime.format('MM-DD')}`;

        // Fill data.
        await this.fillBasicInfo(
          newWorksheet, currentFromTime, currentToTime, agency, patient, caregiver, sampleEvv);
        await this.fillDutyInfo(newWorksheet, evvsThisWeek);

        // Move to next week.
        currentFromTime = currentFromTime.add(7, 'd');
      }

      // Remove the template worksheet.
      workbook.removeWorksheet(workbook.worksheets[0].id);

      // Trigger a file save dialog.
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), 'duty_sheet.xlsx');
    } finally {
      this.setState({
        isExporting: false,
      });
    }
  }

  downloadDutySheetTemplate() {
    return new Promise((resolve, reject) => {
      // Get template.
      const req = new XMLHttpRequest();
      req.open('GET', TEMPLATE_URL, true);
      req.responseType = 'arraybuffer';
      req.onerror = reject;

      req.onload = async (e) => {
        // Read template.
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(req.response);
        resolve(workbook);
      };

      // Send out request.
      req.send();
    });
  }

  async fillBasicInfo(ws, currentFromTime, currentToTime, agency, patient, caregiver, sampleEvv) {
    // Week dates.
    ws.getCell('A2').value = `Week (${misc.toDateUsString(currentFromTime)} - ${misc.toDateUsString(currentToTime)})`;

    // Vendor.
    ws.getCell('C3').value = agency.getString('name');

    // Contract.
    ws.getCell('C4').value = sampleEvv?.getString('companyName');

    // Patient.
    ws.getCell('I3').value = patient.getString('fullName');
    ws.getCell('I4').value = patient.getString('street');
    ws.getCell('I5').value = `${patient.getString('city')}, ${patient.getString('state')} ${patient.getString('zipCode')}`;
    ws.getCell('I6').value = patient.getString('homePhone') || patient.getString('cellPhone');
    ws.getCell('I7').value = patient.getId();

    // Caregiver.
    ws.getCell('C5').value = caregiver.getString('fullName');

    // Team & Coordinator.
    ws.getCell('C6').value = patient.getString('coordinator');
    ws.getCell('C7').value = patient.getString('team');

    // Fill date.
    for (let dow = 1; dow <= 7; dow++) {
      const column = String.fromCharCode('E'.charCodeAt(0) + dow);
      ws.getCell(`${column}10`).value = (currentFromTime.clone().add(dow - 1, 'd')).format('MM/DD');
    }
  }

  async fillDutyInfo(ws, evvsThisWeek) {
    evvsThisWeek.forEach((evv) => {
      const time = evv.getMoment('time');
      const dow = time.isoWeekday(); // 1-7.
      const column = String.fromCharCode('E'.charCodeAt(0) + dow);

      const tasksString = evv.getString('tasks');
      const tasks = tasksString?.split(',') || [];
      tasks.forEach((task) => {
        const row = TASK_TO_ROW[task];
        if (row) {
          ws.getCell(`${column}${row}`).value = 'Y';
        }
      });

      // Fill footer.
      if (evv.getString('type') === 'SHIFT_START') {
        ws.getCell(`${column}53`).value = misc.toTimeString(time); // Evv Time In.
      } else {
        ws.getCell(`${column}54`).value = misc.toTimeString(time); // Evv Time Out.
      }

      const visit = evv.getRemoteObject('scheduleVisit');
      ws.getCell(`${column}55`).value = visit.getString('fromTimeString'); // Confirmed Time In.
      ws.getCell(`${column}56`).value = visit.getString('toTimeString'); // Confirmed Time Out.
      ws.getCell(`${column}57`).value = visit.getString('billHoursString'); // Bill Hours.
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.searchEvvs(values);
      }
    });
  }

  onExportClick() {
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.exportDutySheet(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      caregiverNames,
      patientNames,
      tableKey,
      evvs,
      isSearching,
      isExporting,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="purple_wisteria"
          title="Search EVVs by Criteria"
          icon="check-square"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver Name"
                    placeholder="Select..."
                    options={caregiverNames}
                    showIdAfterName
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="patientId"
                    label="Patient Name"
                    placeholder="Select..."
                    options={patientNames}
                    showIdAfterName
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="fromTime"
                    label="From Time"
                    placeholder="mm/dd/yyyy"
                    extra="Print from the start of this week"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="toTime"
                    label="To Time"
                    placeholder="mm/dd/yyyy"
                    extra="Print to the end of this week"
                    isRequired
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 32px 0 0"
                backgroundColor="purple_wisteria"
                fontSize="14px"
                type="primary"
                disabled={isExporting}
                loading={isSearching}
                htmlType="submit"
              >
                SEARCH
                <Icon type="search" />
              </CustomButton>

              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="purple_wisteria"
                fontSize="14px"
                type="primary"
                disabled={isSearching}
                loading={isExporting}
                onClick={this.onExportClick}
              >
                EXPORT DUTY SHEET
                <Icon type="arrow-right" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {evvs &&
          <TableEvvs
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="EVVs"
            evvs={evvs}
            onEvvInPlaceUpdate={null}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
ExportDutySheetPage.propTypes = propTypes;

export default Form.create()(ExportDutySheetPage);
